import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Booking } from '../types';
import { bookingsAPI } from '../services/api';
import { Calendar, Clock, User as UserIcon, CheckCircle, XCircle, AlertCircle, Phone, Mail, MapPin, Navigation, Bell, ChevronDown, ChevronUp } from 'lucide-react';

const ProviderBookingsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [showCompletedBookings, setShowCompletedBookings] = useState(false);
  
  // Check if user is a driver
  const isDriver = user?.role === 'provider' && (user?.skills?.includes('driver') || Array.isArray(user?.skills) && user.skills.includes('driver'));
  
  // OTP Modal state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      // Fetch with a high limit to get all bookings (no pagination on frontend for now)
      const response = await bookingsAPI.getProviderBookings({ limit: 1000 });
      setBookings(response.data || []);
    } catch (error: any) {
      console.error('Error fetching provider bookings:', error);
      setError(error.response?.data?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId: string, newStatus: 'pending' | 'confirmed' | 'completed' | 'cancelled') => {
    try {
      setUpdatingStatus(bookingId);
      await bookingsAPI.updateBookingStatus(bookingId, newStatus);
      
      // Update the booking in the local state
      setBookings(bookings?.map(booking => 
        (booking._id || booking.id) === bookingId 
          ? { ...booking, status: newStatus }
          : booking
      ) || []);
    } catch (error: any) {
      console.error('Error updating booking status:', error);
      alert(error.response?.data?.message || 'Failed to update booking status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeUntilBooking = (booking: Booking) => {
    const bookingDateTime = booking.tripStartDateTime 
      ? new Date(booking.tripStartDateTime)
      : new Date(booking.bookingDate);
    const now = new Date();
    const diffMs = bookingDateTime.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 0) return { text: 'Started', color: 'text-blue-600', urgent: false };
    if (diffMins <= 15) return { text: `Starting in ${diffMins} mins`, color: 'text-red-600', urgent: true };
    if (diffMins <= 60) return { text: `Starting in ${diffMins} mins`, color: 'text-orange-600', urgent: false };
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return { text: `In ${diffHours} hours`, color: 'text-green-600', urgent: false };
    const diffDays = Math.floor(diffHours / 24);
    return { text: `In ${diffDays} days`, color: 'text-gray-300', urgent: false };
  };

  const sendReminder = async (bookingId: string) => {
    try {
      await bookingsAPI.sendBookingReminder(bookingId);
      alert('Reminder sent to both customer and driver!');
    } catch (error: any) {
      console.error('Error sending reminder:', error);
      alert(error.response?.data?.message || 'Failed to send reminder');
    }
  };

  const viewRoute = (booking: Booking) => {
    const bookingId = booking._id || booking.id;
    if (bookingId) {
      navigate(`/driver-map/${bookingId}`);
    }
  };

  const handleStartTrip = (booking: Booking) => {
    setSelectedBooking(booking);
    setOtp('');
    setShowOtpModal(true);
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6 || !selectedBooking) {
      alert('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setVerifying(true);
      const bookingId = selectedBooking._id || selectedBooking.id;
      await bookingsAPI.verifyTripOTP(bookingId!, otp);
      alert('Trip started successfully!');
      setShowOtpModal(false);
      setOtp('');
      fetchBookings(); // Refresh bookings
    } catch (err: any) {
      alert(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setVerifying(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with User Photo */}
      <div className="bg-gradient-to-r from-black to-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-6">
            {user?.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.name || 'User'} 
                className="h-20 w-20 rounded-full border-4 border-white shadow-lg object-cover"
              />
            ) : (
              <div className="h-20 w-20 rounded-full border-4 border-white shadow-lg bg-white bg-opacity-20 flex items-center justify-center">
                <Calendar className="h-10 w-10 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white">Service Bookings</h1>
              <p className="text-white mt-1">View and manage your customer bookings and appointments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {bookings?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings yet</h3>
            <p className="text-gray-600 mb-6">
              You don't have any customer bookings yet. Your bookings will appear here once customers book your services.
            </p>
          </div>
        ) : (
          <>
            {/* Active Bookings */}
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Active Bookings</h2>
              {(() => {
                const activeBookings = bookings?.filter(b => b.status !== 'completed' && b.status !== 'cancelled') || [];
                
                if (activeBookings.length === 0) {
                  return (
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                      <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No active bookings at the moment</p>
                    </div>
                  );
                }
                
                // Helper function to get start datetime
                const getStartDateTime = (booking: Booking) => {
                  if (booking.tripStartDateTime) {
                    return new Date(booking.tripStartDateTime).getTime();
                  }
                  return new Date(booking.bookingDate).getTime();
                };

                // Helper function to get end datetime
                const getEndDateTime = (booking: Booking) => {
                  if (booking.tripEndDateTime) {
                    return new Date(booking.tripEndDateTime).getTime();
                  }
                  const startDateTime = getStartDateTime(booking);
                  // For driver bookings with numberOfNights, calculate end date
                  const isDriverBooking = booking.serviceType === 'driver' || booking.pickupLocation;
                  if (isDriverBooking && booking.numberOfNights) {
                    return startDateTime + (booking.numberOfNights * 24 * 60 * 60 * 1000);
                  }
                  // Default: assume 1 hour duration if no end time
                  return startDateTime + (60 * 60 * 1000);
                };

                // Helper function to check if booking is current
                const isCurrentBooking = (booking: Booking) => {
                  const now = Date.now();
                  const startDateTime = getStartDateTime(booking);
                  const endDateTime = getEndDateTime(booking);
                  return startDateTime <= now && now <= endDateTime;
                };

                // Find current booking
                const currentBooking = activeBookings.find(b => isCurrentBooking(b));
                
                // If no current booking, find most recently made booking (by createdAt)
                const firstBooking = currentBooking || (activeBookings.length > 0 ? activeBookings.reduce((mostRecent, current) => {
                  const currentCreated = new Date(current.createdAt).getTime();
                  const mostRecentCreated = new Date(mostRecent.createdAt).getTime();
                  return currentCreated > mostRecentCreated ? current : mostRecent;
                }, activeBookings[0]) : null);

                // Separate first booking from the rest
                const remainingBookings = activeBookings.filter(b => 
                  (b._id || b.id) !== (firstBooking?._id || firstBooking?.id)
                );

                // Sort remaining bookings by startdatetime descending
                const sortedRemaining = remainingBookings.sort((a, b) => {
                  const startA = getStartDateTime(a);
                  const startB = getStartDateTime(b);
                  return startB - startA; // Descending order
                });

                // Combine: first booking + sorted remaining
                const sortedActiveBookings = firstBooking 
                  ? [firstBooking, ...sortedRemaining]
                  : sortedRemaining;
                
                return sortedActiveBookings.map((booking) => {
              const timeInfo = booking.status === 'confirmed' ? getTimeUntilBooking(booking) : null;
              const isDriver = booking.serviceType === 'driver' || booking.pickupLocation;
              
              return (
                <div key={booking._id || booking.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-black to-gray-600 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                          {isDriver ? <Navigation className="h-6 w-6 text-white" /> : <Calendar className="h-6 w-6 text-white" />}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            {booking.serviceType || 'Service Booking'}
                          </h3>
                          <p className="text-white text-sm">
                            ID: {(booking.bookingId || booking._id || booking.id)?.toString().slice(-8).toUpperCase()}
                          </p>
                        </div>
                      </div>
                      <div className={`px-4 py-2 rounded-lg border-2 flex items-center space-x-2 ${getStatusColor(booking.status)}`}>
                        {getStatusIcon(booking.status)}
                        <span className="font-semibold text-sm">
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Time Alert for Confirmed Bookings */}
                  {timeInfo && timeInfo.urgent && (
                    <div className="bg-red-50 border-l-4 border-red-500 px-6 py-3 flex items-center">
                      <Bell className="h-5 w-5 text-red-600 mr-3" />
                      <span className="text-red-700 font-semibold">{timeInfo.text}!</span>
                    </div>
                  )}

                  {/* Body */}
                  <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      {/* Customer Info */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <UserIcon className="h-4 w-4 mr-2 text-black" />
                          Customer Details
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p className="font-medium text-gray-900">
                            {booking.customer?.name || (typeof booking.customerId === 'object' ? booking.customerId?.name : null) || 'Customer'}
                          </p>
                          {booking.customer?.phone && (
                            <a href={`tel:${booking.customer.phone}`} className="flex items-center text-black hover:text-gray-800">
                              <Phone className="h-3 w-3 mr-2" />
                              {booking.customer.phone}
                            </a>
                          )}
                          {booking.customer?.email && (
                            <a href={`mailto:${booking.customer.email}`} className="flex items-center text-black hover:text-gray-800">
                              <Mail className="h-3 w-3 mr-2" />
                              {booking.customer.email}
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Booking Info */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-black" />
                          Schedule
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Booked On:</span>
                            <span className="font-medium text-gray-900">
                              {new Date(booking.createdAt).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="border-t border-gray-200 pt-2"></div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Start Date:</span>
                            <span className="font-medium text-gray-900">{formatDate(booking.bookingDate)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Start Time:</span>
                            <span className="font-medium text-gray-900">
                              {booking.tripStartDateTime 
                                ? formatTime(new Date(booking.tripStartDateTime).toTimeString().slice(0, 5))
                                : 'N/A'}
                            </span>
                          </div>
                          {isDriver && booking.numberOfNights !== undefined && booking.numberOfNights > 0 && (
                            <>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">End Date:</span>
                                <span className="font-medium text-gray-900">
                                  {formatDate(new Date(new Date(booking.bookingDate).getTime() + booking.numberOfNights * 24 * 60 * 60 * 1000).toISOString())}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">Duration:</span>
                                <span className="font-medium text-gray-900">
                                  {booking.numberOfNights} night{booking.numberOfNights > 1 ? 's' : ''}
                                </span>
                              </div>
                            </>
                          )}
                          {!isDriver && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">End Time:</span>
                              <span className="font-medium text-gray-900">
                                {booking.tripEndDateTime 
                                  ? formatTime(new Date(booking.tripEndDateTime).toTimeString().slice(0, 5))
                                  : 'N/A'}
                              </span>
                            </div>
                          )}
                          {timeInfo && (
                            <div className={`flex items-center justify-between pt-2 border-t border-gray-200 ${timeInfo.color} font-semibold`}>
                              <span>Status:</span>
                              <span>{timeInfo.text}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Route Info for Driver Bookings */}
                    {isDriver && (booking.pickupLocation || booking.dropoffLocation) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                          <MapPin className="h-4 w-4 mr-2" />
                          Trip Route
                        </h4>
                        <div className="space-y-3">
                          {booking.pickupLocation && (
                            <div className="flex items-start">
                              <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                                <MapPin className="h-4 w-4 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-gray-600 mb-1">Pickup Location</p>
                                <p className="text-sm font-medium text-gray-900">{booking.pickupLocation.address || 'Pickup location'}</p>
                              </div>
                            </div>
                          )}
                          {booking.pickupLocation && booking.dropoffLocation && (
                            <div className="ml-4 border-l-2 border-gray-300 h-4"></div>
                          )}
                          {booking.dropoffLocation && (
                            <div className="flex items-start">
                              <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                                <MapPin className="h-4 w-4 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-gray-600 mb-1">Drop-off Location</p>
                                <p className="text-sm font-medium text-gray-900">{booking.dropoffLocation.address || 'Dropoff location'}</p>
                              </div>
                            </div>
                          )}
                          {booking.totalDistance && (
                            <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                              <span className="text-xs text-gray-600">Estimated Distance:</span>
                              <span className="text-sm font-semibold text-blue-900">{typeof booking.totalDistance === 'number' ? booking.totalDistance.toFixed(2) : booking.totalDistance || '0.00'} km</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Payment & Notes */}
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                        <span className="text-sm text-gray-600">Amount</span>
                        <span className="text-xl font-bold text-green-600">₹{typeof booking.totalAmount === 'number' ? booking.totalAmount.toFixed(2) : booking.totalAmount || '0.00'}</span>
                      </div>
                      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                        <span className="text-sm text-gray-600">Payment</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          booking.paymentStatus === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : booking.paymentStatus === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {booking.paymentStatus}
                        </span>
                      </div>
                    </div>

                    {booking.notes && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-gray-900">Note:</span> {booking.notes}
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      {booking.status === 'confirmed' && isDriver && !booking.otpVerified && (
                        <>
                          <button
                            onClick={() => handleStartTrip(booking)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center font-medium shadow-sm"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Start Trip (Enter OTP)
                          </button>
                          <button
                            onClick={() => viewRoute(booking)}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium shadow-sm"
                          >
                            <Navigation className="h-4 w-4 mr-2" />
                            Navigate to Pickup
                          </button>
                        </>
                      )}
                      
                      {booking.status === 'confirmed' && isDriver && booking.otpVerified && (
                        <div className="w-full bg-green-50 border-2 border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-center text-green-700">
                            <CheckCircle className="h-5 w-5 mr-2" />
                            <span className="font-semibold">Trip in Progress</span>
                          </div>
                          <p className="text-sm text-green-600 text-center mt-1">
                            Started: {booking.tripActualStartDateTime ? new Date(booking.tripActualStartDateTime).toLocaleTimeString() : 'Unknown'}
                          </p>
                        </div>
                      )}
                      
                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => sendReminder(booking._id || booking.id || '')}
                          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center font-medium shadow-sm"
                        >
                          <Bell className="h-4 w-4 mr-2" />
                          Send Reminder
                        </button>
                      )}

                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateBookingStatus(booking._id || booking.id || '', 'confirmed')}
                            disabled={updatingStatus === (booking._id || booking.id)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm"
                          >
                            {updatingStatus === (booking._id || booking.id) ? 'Updating...' : '✓ Confirm Booking'}
                          </button>
                          <button
                            onClick={() => updateBookingStatus(booking._id || booking.id || '', 'cancelled')}
                            disabled={updatingStatus === (booking._id || booking.id)}
                            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm"
                          >
                            {updatingStatus === (booking._id || booking.id) ? 'Updating...' : '✕ Cancel'}
                          </button>
                        </>
                      )}
                      
                      {booking.status === 'confirmed' && booking.otpVerified && (
                        <button
                          onClick={() => updateBookingStatus(booking._id || booking.id || '', 'completed')}
                          disabled={updatingStatus === (booking._id || booking.id)}
                          className="flex-1 sm:flex-none px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm"
                        >
                          {updatingStatus === (booking._id || booking.id) ? 'Updating...' : '✓ Complete Trip'}
                        </button>
                      )}
                      
                      {booking.status === 'confirmed' && !booking.otpVerified && isDriver && (
                        <div className="w-full bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                          <div className="flex items-center justify-center text-yellow-800">
                            <AlertCircle className="h-5 w-5 mr-2" />
                            <span className="font-semibold">Start trip first to complete it</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
                });
              })()}
            </div>

            {/* Completed Bookings - Expandable Section */}
            {(() => {
              const completedBookings = bookings?.filter(b => b.status === 'completed' || b.status === 'cancelled') || [];
              
              // Sort completed bookings by trip end date/time
              const sortedCompletedBookings = completedBookings.sort((a, b) => {
                const getEndDateTime = (booking: typeof a) => {
                  if (booking.numberOfNights && booking.numberOfNights > 0) {
                    const startDate = new Date(booking.bookingDate);
                    return startDate.getTime() + booking.numberOfNights * 24 * 60 * 60 * 1000;
                  }
                  return new Date(booking.bookingDate).getTime();
                };
                
                return getEndDateTime(b) - getEndDateTime(a);
              });
              
              if (sortedCompletedBookings.length === 0) return null;
              
              return (
              <div className="mt-8">
                <button
                  onClick={() => setShowCompletedBookings(!showCompletedBookings)}
                  className="w-full bg-white rounded-lg shadow-sm p-4 flex items-center justify-between hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div className="text-left">
                      <h2 className="text-lg font-bold text-gray-900">Completed Bookings</h2>
                      <p className="text-sm text-gray-600">
                        {sortedCompletedBookings.length} booking(s)
                      </p>
                    </div>
                  </div>
                  {showCompletedBookings ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {showCompletedBookings && (
                  <div className="mt-4 space-y-6">
                    {sortedCompletedBookings.map((booking) => {
                      const isDriver = booking.serviceType === 'driver' || booking.pickupLocation;
                      
                      return (
                        <div key={booking._id || booking.id} className="bg-white rounded-lg shadow-md opacity-75 hover:opacity-100 transition-opacity overflow-hidden">
                          {/* Status Banner */}
                          <div className={`px-6 py-3 ${
                            booking.status === 'completed' ? 'bg-green-50 border-l-4 border-green-500' : 'bg-gray-50 border-l-4 border-gray-500'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                {booking.status === 'completed' ? (
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-gray-600" />
                                )}
                                <div>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {booking.status === 'completed' ? 'Completed' : 'Cancelled'}
                                  </span>
                                  <p className="text-xs text-gray-600">
                                    Booking ID: {(booking.bookingId || booking._id || booking.id)?.toString().slice(-8).toUpperCase()}
                                  </p>
                                </div>
                              </div>
                              <span className="text-sm font-bold text-gray-900">₹{typeof booking.totalAmount === 'number' ? booking.totalAmount.toFixed(2) : booking.totalAmount || '0.00'}</span>
                            </div>
                          </div>

                          <div className="p-6">
                            <div className="grid md:grid-cols-2 gap-6">
                              {/* Customer Info */}
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                  <UserIcon className="h-4 w-4 mr-2 text-black" />
                                  Customer Details
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <p className="font-medium text-gray-900">
                                    {booking.customer?.name || (typeof booking.customerId === 'object' ? booking.customerId?.name : null) || 'Customer'}
                                  </p>
                                </div>
                              </div>

                              {/* Schedule */}
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                  <Clock className="h-4 w-4 mr-2 text-black" />
                                  Schedule
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">Date:</span>
                                    <span className="font-medium text-gray-900">{formatDate(booking.bookingDate)}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">Time:</span>
                                    <span className="font-medium text-gray-900">
                                      {booking.tripStartDateTime 
                                        ? formatTime(new Date(booking.tripStartDateTime).toTimeString().slice(0, 5))
                                        : 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Route Info for Driver Bookings */}
                            {isDriver && (booking.pickupLocation || booking.dropoffLocation) && (
                              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                                  <MapPin className="h-4 w-4 mr-2" />
                                  Trip Route
                                </h4>
                                <div className="space-y-3 text-sm">
                                  {booking.pickupLocation && (
                                    <div className="flex items-start">
                                      <div className="bg-green-100 rounded-full p-1 mr-3 flex-shrink-0">
                                        <MapPin className="h-3 w-3 text-green-700" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900">Pickup</p>
                                        <p className="text-gray-600">{booking.pickupLocation.address || 'Address not available'}</p>
                                      </div>
                                    </div>
                                  )}
                                  {booking.dropoffLocation && (
                                    <div className="flex items-start">
                                      <div className="bg-red-100 rounded-full p-1 mr-3 flex-shrink-0">
                                        <MapPin className="h-3 w-3 text-red-700" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900">Drop-off</p>
                                        <p className="text-gray-600">{booking.dropoffLocation.address || 'Address not available'}</p>
                                      </div>
                                    </div>
                                  )}
                                  {booking.totalDistance && (
                                    <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                                      <span className="text-blue-900 font-medium">Total Distance:</span>
                                      <span className="text-blue-900 font-bold">{typeof booking.totalDistance === 'number' ? booking.totalDistance.toFixed(2) : booking.totalDistance || '0.00'} km</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })()}
          </>
        )}
      </div>

      {/* OTP Modal */}
      {showOtpModal && selectedBooking && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowOtpModal(false)}></div>
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <button
                onClick={() => setShowOtpModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>

              <div className="text-center mb-6">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Start Trip</h2>
                <p className="text-gray-600">
                  Ask the customer for their 6-digit OTP to verify and start the trip
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Important:</strong> Verify the customer's identity before accepting the OTP.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer OTP
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    autoFocus
                  />
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    Enter the 6-digit code provided by the customer
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowOtpModal(false)}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVerifyOTP}
                    disabled={verifying || otp.length !== 6}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
                  >
                    {verifying ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Verify & Start
                      </>
                    )}
                  </button>
                </div>
              </div>

              {selectedBooking.customer && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Customer Details:</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><strong>Name:</strong> {selectedBooking.customer.name || 'N/A'}</p>
                    {selectedBooking.customer.phone && (
                      <p><strong>Phone:</strong> {selectedBooking.customer.phone}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderBookingsPage; 